import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI, userAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import './SedmicaGameRoom.css';

interface SedmicaGame {
  id: string;
  name: string;
  hostId: string;
  type: 'public' | 'private' | 'friends';
  gameVariant: 'sedmica';
  status: 'waiting' | 'playing' | 'finished';
  maxPlayers: number;
  players: string[];
  spectators: string[];
  createdAt: string;
}

type Suit = 'acorn' | 'ball' | 'heart' | 'leaf';

interface HungarianCard {
  id: string;
  rank: number | string;
  suit: Suit;
  image: string;
}

interface SedmicaPlayer {
  id: string;
  name: string;
  cards: HungarianCard[];
  team: 0 | 1;
  position: number;
}

interface TrickRound {
  cards: { playerId: string; card: HungarianCard }[];
}

interface CurrentTrick {
  leaderId: string;
  leadCard: HungarianCard;
  rounds: TrickRound[];
  winnerId: string | null;
  winningTeam: 0 | 1 | null;
  continued: boolean;
}

interface SedmicaGameState {
  id: string;
  players: SedmicaPlayer[];
  stock: HungarianCard[];
  gamePhase: 'waiting' | 'playing' | 'trick-continuation' | 'deal-finished' | 'game-over';
  currentPlayerIndex: number;
  dealerIndex: number;
  currentTrick: CurrentTrick | null;
  trickRound: number;
  trickCardsPlayed: number;
  teamTrickCards: [HungarianCard[], HungarianCard[]];
  teamScores: [number, number];
  dealScores: [number, number];
  lastTrickWinnerTeam: 0 | 1 | null;
  currentDeal: number;
  targetScore: number;
  gameVariant: 'sedmica';
  winner: number | null;
  partnerRevealDone: boolean;
}

const SedmicaGameRoom: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [game, setGame] = useState<SedmicaGame | null>(null);
  const [gameState, setGameState] = useState<SedmicaGameState | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [playerUsers, setPlayerUsers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const getCardImage = (card: HungarianCard | null | undefined): string => {
    if (!card) return '/assets/back.png';
    if (card.image) return card.image;
    return `/assets/${card.id}.png`;
  };

  const loadPlayerUsers = async (playerIds: string[]) => {
    try {
      const results = await Promise.all(
        playerIds.map(async (pid) => {
          try {
            const res = await userAPI.getById(pid);
            return { [pid]: res.data };
          } catch { return null; }
        })
      );
      const map: Record<string, any> = {};
      results.forEach(r => { if (r) Object.assign(map, r); });
      setPlayerUsers(prev => ({ ...prev, ...map }));
    } catch { /* ignore */ }
  };

  const loadGame = useCallback(async () => {
    if (!gameId) return;
    try {
      const response = await gamesAPI.getById(gameId);
      setGame(response.data);
      await loadPlayerUsers(response.data.players);
    } catch (err) {
      console.error('Error loading Sedmica game:', err);
      setError('Failed to load game');
    }
  }, [gameId]);

  // Initialize game - auto-sit host, connect socket
  useEffect(() => {
    if (!gameId || !user) return;

    let socketRef: Socket | null = null;

    const initializeGame = async () => {
      try {
        const gameResponse = await gamesAPI.getById(gameId);
        const gameData = gameResponse.data;
        setGame(gameData);
        await loadPlayerUsers(gameData.players);

        // Host auto-joins and auto-sits
        if (gameData.hostId === user.id && gameData.status === 'waiting') {
          const isInGame = gameData.players.includes(user.id) || gameData.spectators?.includes(user.id);
          if (!isInGame) {
            try { await gamesAPI.join(gameId, false); } catch (e) { /* already joined */ }
          }
          if (!gameData.players.includes(user.id)) {
            try {
              await gamesAPI.sit(gameId);
              const refreshed = await gamesAPI.getById(gameId);
              setGame(refreshed.data);
            } catch (e) { /* already sitting */ }
          }
        }

        // Connect socket
        const token = localStorage.getItem('token');
        if (token) {
          const newSocket = io('/', { auth: { token } });
          socketRef = newSocket;

          newSocket.on('connect', () => {
            newSocket.emit('joinGameRoom', { gameId });
            if (gameData.status === 'playing') {
              newSocket.emit('getGameState', { gameId });
            }
          });

          newSocket.on('gameStateUpdated', (data: any) => {
            if (data.gameId === gameId) setGameState(data.gameState);
          });

          newSocket.on('gameStateReceived', (data: any) => {
            if (data.gameId === gameId) setGameState(data.gameState);
          });

          newSocket.on('gameUpdate', (data: any) => {
            if (data.gameId === gameId) loadGame();
          });

          newSocket.on('playerJoined', () => loadGame());
          newSocket.on('playerSat', () => loadGame());
          newSocket.on('playerLeft', () => loadGame());

          newSocket.on('gameStarted', (data: any) => {
            if (data.gameState) setGameState(data.gameState);
            loadGame();
          });

          newSocket.on('gameError', (data: any) => {
            setMessage(data.message);
            setTimeout(() => setMessage(null), 5000);
          });

          setSocket(newSocket);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error initializing Sedmica game:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    };

    initializeGame();

    return () => {
      if (socketRef) socketRef.disconnect();
    };
  }, [gameId, user, loadGame]);

  // Actions
  const handlePlayCard = useCallback((cardIndex: number) => {
    if (!socket || !gameId) return;
    if (gameState?.gamePhase === 'trick-continuation') {
      socket.emit('sedmicaContinueTrick', { gameId, cardIndex });
    } else {
      socket.emit('sedmicaPlayCard', { gameId, cardIndex });
    }
  }, [socket, gameId, gameState?.gamePhase]);

  const handlePassTrick = useCallback(() => {
    if (!socket || !gameId) return;
    socket.emit('sedmicaPassTrick', { gameId });
  }, [socket, gameId]);

  const handleStartNextDeal = useCallback(() => {
    if (!socket || !gameId) return;
    socket.emit('sedmicaStartNextDeal', { gameId });
  }, [socket, gameId]);

  const handleSit = async () => {
    if (!gameId || !user) return;
    try {
      const isInGame = game!.players.includes(user.id) || game!.spectators?.includes(user.id);
      if (!isInGame) {
        try { await gamesAPI.join(gameId, false); } catch (e) { /* already joined */ }
      }
      await gamesAPI.sit(gameId);
      loadGame();
    } catch (err) {
      console.error('Error sitting:', err);
    }
  };

  const handleStartGame = async () => {
    if (!gameId) return;
    try {
      await gamesAPI.start(gameId);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to start game';
      setMessage(msg);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  // Derived state
  const isHost = game?.hostId === user?.id;
  const myPlayer = gameState?.players.find(p => p.id === user?.id);
  const myTeam = myPlayer?.team;
  const isMyTurn = gameState?.players[gameState.currentPlayerIndex]?.id === user?.id;
  const isTrickContinuation = gameState?.gamePhase === 'trick-continuation';
  const amLeader = isTrickContinuation && gameState?.currentTrick?.leaderId === user?.id;

  const isCardPlayable = (card: HungarianCard): boolean => {
    if (!isMyTurn) return false;
    if (gameState?.gamePhase === 'playing' && !gameState.currentTrick) return true;
    if (gameState?.gamePhase === 'playing' && gameState.currentTrick) return true;
    if (isTrickContinuation && amLeader) {
      const ledRank = gameState?.currentTrick?.leadCard.rank;
      return card.rank === ledRank || card.rank === 7;
    }
    return false;
  };

  // ─── Render: Waiting Table (seats) ────────────────────────────

  const renderWaitingTable = () => {
    const maxPlayers = game?.maxPlayers || 2;
    const seatedCount = game?.players.length || 0;
    const canStart = seatedCount === 2 || seatedCount === 4;
    return (
      <div className="sedmica-waiting-table">
        <div className="sedmica-waiting-info">
          <h2>Sedmica</h2>
          <p>Waiting for players... {seatedCount}/{maxPlayers}</p>
          {isHost && <p className="sedmica-host-badge">You are the host</p>}
        </div>
        <div className="sedmica-seats">
          {Array.from({ length: maxPlayers }).map((_, seatIndex) => {
            const playerId = game?.players[seatIndex];
            const isOccupied = !!playerId;
            const isMe = playerId === user?.id;
            const alreadySeated = game?.players.includes(user?.id || '');

            return (
              <div key={seatIndex} className={`sedmica-seat ${isOccupied ? 'occupied' : 'empty'} ${isMe ? 'me' : ''}`}>
                {isOccupied ? (
                  <>
                    <div className="sedmica-seat-cards">
                      {Array.from({ length: 4 }).map((_, ci) => (
                        <img key={ci} src="/assets/back.png" alt="Card" className="sedmica-seat-card-back" />
                      ))}
                    </div>
                    <div className="sedmica-seat-name">
                      {playerUsers[playerId]?.firstName
                        ? `${playerUsers[playerId].firstName} ${playerUsers[playerId].lastName || ''}`.trim()
                        : `Player ${seatIndex + 1}`}
                      {isMe && <span className="sedmica-you-badge"> (You)</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sedmica-seat-placeholder">Empty Seat</div>
                    <button
                      className="sedmica-sit-btn"
                      onClick={handleSit}
                      disabled={!!alreadySeated}
                    >
                      Sit Here
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {isHost && (
          <div className="sedmica-waiting-actions">
            <button
              className="sedmica-btn sedmica-btn-start"
              onClick={handleStartGame}
              disabled={!canStart}
            >
              ▶ Start Game ({seatedCount}/{maxPlayers})
            </button>
            {!canStart && (
              <p className="sedmica-start-hint">Sedmica needs 2 or 4 seated players to start.</p>
            )}
          </div>
        )}
        <button className="sedmica-btn-back" onClick={() => navigate('/dashboard')} style={{ marginTop: '20px' }}>
          ← Back to Dashboard
        </button>
      </div>
    );
  };

  // ─── Render: Playing Table ────────────────────────────────────

  const renderPlayerHand = () => {
    if (!myPlayer) return null;
    return (
      <div className="sedmica-hand">
        {myPlayer.cards.map((card, idx) => {
          const playable = isCardPlayable(card);
          return (
            <img
              key={card.id + idx}
              src={getCardImage(card)}
              alt={card.id}
              className={`sedmica-card ${playable ? 'playable' : 'disabled'} ${isMyTurn ? 'my-turn' : ''}`}
              onClick={() => playable && handlePlayCard(idx)}
            />
          );
        })}
      </div>
    );
  };

  const renderCurrentTrick = () => {
    if (!gameState?.currentTrick) return null;
    const trick = gameState.currentTrick;
    const currentRound = trick.rounds[trick.rounds.length - 1];

    return (
      <div className="sedmica-trick-area">
        <div className="sedmica-trick-label">
          Trick (Round {trick.rounds.length}/{Math.min(trick.rounds.length + 1, 4)})
          {trick.continued && <span className="sedmica-continued"> - Continued!</span>}
        </div>
        <div className="sedmica-trick-cards">
          {currentRound.cards.map((play, i) => (
            <div key={play.playerId + i} className="sedmica-trick-card-wrapper">
              <img src={getCardImage(play.card)} alt={play.card.id} className="sedmica-trick-card" />
              <span className="sedmica-trick-card-name">
                {gameState.players.find(p => p.id === play.playerId)?.name || 'Unknown'}
              </span>
            </div>
          ))}
        </div>
        {trick.rounds.length > 1 && (
          <div className="sedmica-previous-rounds">
            <span className="sedmica-prev-label">Previous rounds:</span>
            {trick.rounds.slice(0, -1).map((round, ri) => (
              <div key={ri} className="sedmica-prev-round">
                {round.cards.map((play, ci) => (
                  <img key={ci} src={getCardImage(play.card)} alt="" className="sedmica-prev-card" />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGameInfo = () => {
    if (!gameState) return null;
    const dealer = gameState.players[gameState.dealerIndex];
    const currentPlayerState = gameState.players[gameState.currentPlayerIndex];

    return (
      <div className="sedmica-game-info">
        <div className="sedmica-info-row">
          <span>Deal #{gameState.currentDeal}</span>
          <span>Target: {gameState.targetScore} pts</span>
          <span>Stock: {gameState.stock.length} cards</span>
        </div>
        <div className="sedmica-info-row">
          <span>Dealer: {dealer?.name}</span>
          <span className="sedmica-current-turn">
            Turn: {currentPlayerState?.name} {currentPlayerState?.id === user?.id && '(YOU)'}
          </span>
        </div>
      </div>
    );
  };

  const renderScoreboard = () => {
    if (!gameState) return null;
    const team0Players = gameState.players.filter(p => p.team === 0).map(p => p.name).join(' & ');
    const team1Players = gameState.players.filter(p => p.team === 1).map(p => p.name).join(' & ');

    return (
      <div className="sedmica-scoreboard">
        <div className={`sedmica-team ${myTeam === 0 ? 'my-team' : ''}`}>
          <div className="sedmica-team-name">Team A</div>
          <div className="sedmica-team-players">{team0Players}</div>
          <div className="sedmica-team-score">{gameState.teamScores[0]} pts</div>
          <div className="sedmica-team-deal-score">This deal: {gameState.dealScores[0]}</div>
        </div>
        <div className={`sedmica-team ${myTeam === 1 ? 'my-team' : ''}`}>
          <div className="sedmica-team-name">Team B</div>
          <div className="sedmica-team-players">{team1Players}</div>
          <div className="sedmica-team-score">{gameState.teamScores[1]} pts</div>
          <div className="sedmica-team-deal-score">This deal: {gameState.dealScores[1]}</div>
        </div>
      </div>
    );
  };

  const renderTrickContinuation = () => {
    if (!isTrickContinuation || !amLeader) return null;
    return (
      <div className="sedmica-continuation-panel">
        <p>Opponent is winning the trick! You can continue or pass.</p>
        <p className="sedmica-continuation-hint">
          Play a card of the same rank as your lead or a 7 to continue, or pass to let opponents take it.
        </p>
        <button className="sedmica-btn sedmica-btn-pass" onClick={handlePassTrick}>
          Pass (Let them take it)
        </button>
      </div>
    );
  };

  const renderDealFinished = () => {
    if (gameState?.gamePhase !== 'deal-finished') return null;
    return (
      <div className="sedmica-deal-finished">
        <h3>Deal #{gameState.currentDeal} Finished!</h3>
        <div className="sedmica-deal-results">
          <p>Team A scored: <strong>{gameState.dealScores[0]}</strong> points this deal</p>
          <p>Team B scored: <strong>{gameState.dealScores[1]}</strong> points this deal</p>
          <hr />
          <p>Cumulative - Team A: <strong>{gameState.teamScores[0]}</strong> / {gameState.targetScore}</p>
          <p>Cumulative - Team B: <strong>{gameState.teamScores[1]}</strong> / {gameState.targetScore}</p>
        </div>
        <button className="sedmica-btn sedmica-btn-next" onClick={handleStartNextDeal}>
          Start Next Deal
        </button>
      </div>
    );
  };

  const renderGameOver = () => {
    if (gameState?.gamePhase !== 'game-over') return null;
    const winnerTeam = gameState.winner;
    const winners = gameState.players.filter(p => p.team === winnerTeam).map(p => p.name);
    const isMyWin = myTeam === winnerTeam;

    return (
      <div className="sedmica-game-over">
        <h2>{isMyWin ? '🎉 You Won!' : '😞 You Lost'}</h2>
        <p>Winners: <strong>{winners.join(' & ')}</strong></p>
        <div className="sedmica-final-scores">
          <p>Team A: {gameState.teamScores[0]} pts</p>
          <p>Team B: {gameState.teamScores[1]} pts</p>
        </div>
        <button className="sedmica-btn" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  };

  const renderOtherPlayers = () => {
    if (!gameState || !user) return null;
    const others = gameState.players.filter(p => p.id !== user.id);

    return (
      <div className="sedmica-opponents">
        {others.map(player => (
          <div key={player.id} className={`sedmica-opponent ${gameState.players[gameState.currentPlayerIndex]?.id === player.id ? 'active-player' : ''}`}>
            <div className="sedmica-opponent-name">
              {player.name}
              {player.team === myTeam && <span className="sedmica-partner-badge">Partner</span>}
            </div>
            <div className="sedmica-opponent-cards">
              {player.cards.map((card, i) => (
                <img
                  key={i}
                  src={getCardImage(card)}
                  alt="card"
                  className="sedmica-opponent-card"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────

  if (loading) return <div className="sedmica-loading">Loading game...</div>;
  if (error) return <div className="sedmica-error">{error}</div>;

  // Waiting state - show seats
  if (game?.status === 'waiting' && !gameState) {
    return renderWaitingTable();
  }

  // Playing state
  if (!gameState) {
    return (
      <div className="sedmica-waiting">
        <h2>Sedmica</h2>
        <p>Connecting to game...</p>
      </div>
    );
  }

  return (
    <div className="sedmica-room">
      {/* Header */}
      <div className="sedmica-header">
        <button className="sedmica-btn-back" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2>Sedmica</h2>
        {renderGameInfo()}
      </div>

      {/* Messages */}
      {message && <div className="sedmica-message">{message}</div>}

      {/* Scoreboard — desktop/sidebar only; hidden on mobile table view */}
      <div className="sedmica-scoreboard-wrap">
        {renderScoreboard()}
      </div>

      {/* Opponents */}
      {renderOtherPlayers()}

      {/* Game Table */}
      <div className="sedmica-table">
        {renderCurrentTrick()}
        {renderTrickContinuation()}
        {renderDealFinished()}
        {renderGameOver()}

        {/* Stock indicator */}
        {gameState.gamePhase === 'playing' && gameState.stock.length > 0 && (
          <div className="sedmica-stock">
            <img src="/assets/back.png" alt="Stock" className="sedmica-stock-card" />
            <span className="sedmica-stock-count">{gameState.stock.length}</span>
          </div>
        )}
      </div>

      {/* My Hand */}
      {gameState.gamePhase !== 'game-over' && (
        <div className="sedmica-my-area">
          <div className="sedmica-my-label">
            Your cards {isMyTurn && <span className="sedmica-your-turn">- Your turn!</span>}
            {isTrickContinuation && amLeader && <span className="sedmica-your-turn">- Continue or Pass!</span>}
          </div>
          {renderPlayerHand()}
        </div>
      )}
    </div>
  );
};

export default SedmicaGameRoom;
